import type { PathOptions } from 'leaflet';
import type { ComuneFull } from '@/data';
import type { ChoroplethLayerDef } from './types';
import { colorFor, blendHex, GREY, NO_DATA_COLOR } from './colors';

const BASE_STYLE: PathOptions = {
  weight: 1,
  color: '#666',
  opacity: 0.5,
};

const DIM_STYLE: PathOptions = {
  ...BASE_STYLE,
  fillColor: GREY,
  fillOpacity: 0.15,
  weight: 0.5,
  opacity: 0.3,
};

const NO_DATA_STYLE: PathOptions = {
  ...BASE_STYLE,
  fillColor: NO_DATA_COLOR,
  fillOpacity: 0.4,
};

// Граница, после которой цветная заливка полностью прячется автоматически —
// на этом зуме comune занимает большую часть экрана, цвет уже не информативен,
// важна сама карта.
export const AUTO_HIDE_ZOOM = 15;

// Прозрачность ЦВЕТНОЙ заливки choropleth (обычный режим, без клика).
// Финальная кривая (US-04 в NEXT_STEPS): на zoom 10–11 цвет всё ещё
// читаем (пользователь сравнивает comuni), на 13–14 уходит в фон,
// на 15+ автоматически 0 (см. AUTO_HIDE_ZOOM).
function fillOpacityScale(zoom: number): number {
  if (zoom <= 8) return 1.0;
  if (zoom <= 9) return 0.85;
  if (zoom <= 10) return 0.70;
  if (zoom <= 11) return 0.55;
  if (zoom <= 12) return 0.38;
  if (zoom <= 13) return 0.22;
  if (zoom <= 14) return 0.10;
  return 0.0;
}

// Прозрачность HATCH-заливки при hover. Намеренно лёгкая (~⅓ от прежних
// значений) — полоски обозначают наведение, но не перекрывают карту.
export function hatchFillOpacity(zoom: number): number {
  if (zoom <= 9) return 0.13;
  if (zoom <= 10) return 0.17;
  if (zoom <= 12) return 0.20;
  if (zoom <= 14) return 0.22;
  return 0.23;
}

// Когда какой-то регион кликнут — соседи становятся ещё прозрачнее,
// чтобы фокус был на кликнутом, но заливка у них всё равно остаётся.
const ISOLATE_DIM_FACTOR = 0.6;

// US-05 / US-12: толщина и прозрачность border'а зависят от view + zoom.
// На zoom 14+ border почти невидим — иначе создаёт «жёсткую сетку» поверх
// подложки, когда comune занимает много экрана и пользователь читает дороги.
// SELECTED (weight 2.5) и HOVER (weight 2) применяются поверх в ChoroplethLayer.
function strokeScale(
  zoom: number,
  view: 'regions' | 'provinces' | 'districts' | 'comuni',
): { weight: number; opacity: number } {
  if (view === 'regions') return { weight: 1.4, opacity: 0.75 };
  if (view === 'provinces') return { weight: 1.2, opacity: 0.7 };
  if (view === 'districts') return { weight: 1.1, opacity: 0.65 };
  if (zoom <= 13) return { weight: 0.8, opacity: 0.5 };
  return { weight: 0.4, opacity: 0.25 };
}

// Среднее значение метрики по району, взвешенное по населению коммун.
// Для intensive-метрик (температура, плотность, %, интернет) это корректнее
// простого среднего: большой город не тонет в десятках мелких альпийских
// коммун. Падает на простое среднее, если население недоступно.
function popWeightedMean(
  comuni: ComuneFull[],
  layerDef: ChoroplethLayerDef,
): number | null {
  let wsum = 0;
  let vwsum = 0;
  let plainSum = 0;
  let count = 0;
  for (const c of comuni) {
    const v = layerDef.getValue(c);
    if (v === null || !isFinite(v)) continue;
    const w = c.pop > 0 ? c.pop : 0;
    wsum += w;
    vwsum += v * w;
    plainSum += v;
    count++;
  }
  if (count === 0) return null;
  return wsum > 0 ? vwsum / wsum : plainSum / count;
}

export function calcComuneStyle(
  istat: string,
  layerDef: ChoroplethLayerDef,
  matchSet: ReadonlySet<string>,
  byIstat: Map<string, ComuneFull>,
  zoom = 10,
  openIstat: string | null = null,
  overlayHidden = false,
): PathOptions {
  const comune = byIstat.get(istat);
  if (!comune) return NO_DATA_STYLE;

  const value = layerDef.getValue(comune);
  if (value === null) return NO_DATA_STYLE;

  const color = colorFor(layerDef, value);
  const stroke = strokeScale(zoom, 'comuni');

  // Isolate-mode: кликнутый регион — нормальная заливка + жирная граница.
  // Сам LOCKED_STYLE (weight 2.5, ink-900) применяется поверх через setStyle
  // в ChoroplethLayer, поэтому здесь задаём только fill.
  if (openIstat !== null && istat === openIstat) {
    return {
      ...BASE_STYLE,
      fillColor: color,
      fillOpacity: fillOpacityScale(zoom),
      weight: 1.5,
    };
  }

  // «Только карта»: ручной тоггл ИЛИ автоматически на больших зумах.
  // Locked-регион выше остался — он не скрывается.
  if (overlayHidden || zoom >= AUTO_HIDE_ZOOM) {
    return { ...BASE_STYLE, fillColor: color, fillOpacity: 0, ...stroke };
  }

  const scale = fillOpacityScale(zoom);
  // В isolate-режиме у не-кликнутых заливка остаётся, но дополнительно
  // приглушается — фокус смещается на кликнутый регион.
  const isolateMult = openIstat !== null ? ISOLATE_DIM_FACTOR : 1.0;

  if (matchSet.size > 0 && !matchSet.has(istat)) {
    return {
      ...DIM_STYLE,
      fillOpacity: DIM_STYLE.fillOpacity! * scale * isolateMult,
      weight: Math.max(0.5, stroke.weight * 0.7),
      opacity: stroke.opacity * 0.6,
    };
  }

  // Matched / no-filter: чуть толще чем DEFAULT (выделение результатов поиска).
  const matchedBoost = matchSet.size > 0 ? 1.5 : 1.0;
  return {
    ...BASE_STYLE,
    fillColor: color,
    fillOpacity: (matchSet.size > 0 ? 0.9 : 0.75) * scale * isolateMult,
    weight: stroke.weight * matchedBoost,
    opacity: stroke.opacity,
  };
}

export function calcRegionStyle(
  regCode: string,
  layerDef: ChoroplethLayerDef,
  matchSet: ReadonlySet<string>,
  byReg: Map<string, ComuneFull[]>,
  zoom = 7,
  overlayHidden = false,
): PathOptions {
  const scale = fillOpacityScale(zoom);
  const stroke = strokeScale(zoom, 'regions');
  const comuni = byReg.get(regCode) ?? [];
  if (comuni.length === 0) return { ...NO_DATA_STYLE, fillOpacity: NO_DATA_STYLE.fillOpacity! * scale, ...stroke };

  const values = comuni
    .map((c) => layerDef.getValue(c))
    .filter((v): v is number => v !== null);

  if (values.length === 0) return { ...NO_DATA_STYLE, fillOpacity: NO_DATA_STYLE.fillOpacity! * scale, ...stroke };

  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const color = colorFor(layerDef, mean);

  if (overlayHidden) {
    return { ...BASE_STYLE, fillColor: color, fillOpacity: 0, ...stroke };
  }

  if (matchSet.size === 0) {
    return { ...BASE_STYLE, fillColor: color, fillOpacity: 0.75 * scale, ...stroke };
  }

  // DIM mode: blend toward grey based on match ratio in this region.
  const matchedCount = comuni.filter((c) => matchSet.has(c.istat)).length;
  const matchRatio = matchedCount / comuni.length;
  if (matchRatio === 0) return { ...DIM_STYLE, fillOpacity: 0.2 * scale, ...stroke };

  const dimColor = blendHex(GREY, color, matchRatio);
  return {
    ...BASE_STYLE,
    fillColor: dimColor,
    fillOpacity: (0.25 + matchRatio * 0.5) * scale,
    ...stroke,
  };
}

// Промежуточный уровень (SLL / comunità comprensoriale). Параллель
// calcProvStyle, но агрегат — взвешенное по населению среднее (popWeightedMean),
// keyed by dist_code через byDist.
export function calcDistrictStyle(
  distCode: string,
  layerDef: ChoroplethLayerDef,
  matchSet: ReadonlySet<string>,
  byDist: Map<string, ComuneFull[]>,
  zoom = 9,
  overlayHidden = false,
): PathOptions {
  const scale = fillOpacityScale(zoom);
  const stroke = strokeScale(zoom, 'districts');
  const comuni = byDist.get(distCode) ?? [];
  if (comuni.length === 0) return { ...NO_DATA_STYLE, ...stroke };

  const mean = popWeightedMean(comuni, layerDef);
  if (mean === null) return { ...NO_DATA_STYLE, ...stroke };

  const color = colorFor(layerDef, mean);

  if (overlayHidden) {
    return { ...BASE_STYLE, fillColor: color, fillOpacity: 0, ...stroke };
  }

  if (matchSet.size === 0) {
    return { ...BASE_STYLE, fillColor: color, fillOpacity: 0.7 * scale, ...stroke };
  }

  const matchedCount = comuni.filter((c) => matchSet.has(c.istat)).length;
  const matchRatio = matchedCount / comuni.length;
  if (matchRatio === 0) return { ...DIM_STYLE, fillOpacity: 0.2 * scale, ...stroke };

  const dimColor = blendHex(GREY, color, matchRatio);
  return {
    ...BASE_STYLE,
    fillColor: dimColor,
    fillOpacity: (0.25 + matchRatio * 0.5) * scale,
    ...stroke,
  };
}

export function calcProvStyle(
  provAcr: string,
  layerDef: ChoroplethLayerDef,
  matchSet: ReadonlySet<string>,
  byProv: Map<string, ComuneFull[]>,
  zoom = 10,
  overlayHidden = false,
): PathOptions {
  const scale = fillOpacityScale(zoom);
  const comuni = byProv.get(provAcr) ?? [];
  if (comuni.length === 0) return NO_DATA_STYLE;

  const values = comuni
    .map((c) => layerDef.getValue(c))
    .filter((v): v is number => v !== null);

  if (values.length === 0) return NO_DATA_STYLE;

  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const color = colorFor(layerDef, mean);
  const stroke = strokeScale(zoom, 'provinces');

  if (overlayHidden) {
    return { ...BASE_STYLE, fillColor: color, fillOpacity: 0, ...stroke };
  }

  if (matchSet.size === 0) {
    return { ...BASE_STYLE, fillColor: color, fillOpacity: 0.7 * scale, ...stroke };
  }

  // DIM mode: blend toward grey based on match ratio in this province
  const matchedCount = comuni.filter((c) => matchSet.has(c.istat)).length;
  const matchRatio = matchedCount / comuni.length;

  if (matchRatio === 0) return { ...DIM_STYLE, fillOpacity: 0.2 * scale, ...stroke };

  const dimColor = blendHex(GREY, color, matchRatio);
  return {
    ...BASE_STYLE,
    fillColor: dimColor,
    fillOpacity: (0.25 + matchRatio * 0.5) * scale,
    ...stroke,
  };
}
